import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useNotification } from "@/shared/hooks/useNotification";
import { usePaymentTerms, useVatRates } from "@/shared/hooks/useMetaData";
import { useGenerateSalesInvoiceFromOrder } from "@/features/invoicing/hooks/useInvoicing";
import type { Order } from "../../services/ordersApi";

interface GenerateInvoiceFromOrderDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const GenerateInvoiceFromOrderDialog = ({
  order,
  open,
  onOpenChange,
}: GenerateInvoiceFromOrderDialogProps) => {
  const navigate = useNavigate();
  const notification = useNotification();
  const generateMutation = useGenerateSalesInvoiceFromOrder();
  const paymentTermsQuery = usePaymentTerms();
  const vatRatesQuery = useVatRates();
  const defaultVatRate =
    vatRatesQuery.data?.find((rate) => rate.name.toLowerCase().includes("standard"))?.rate ??
    vatRatesQuery.data?.[0]?.rate ??
    0;

  const [dueDate, setDueDate] = useState("");
  const [taxRate, setTaxRate] = useState("0");
  const [discountAmount, setDiscountAmount] = useState("0");
  const [terms, setTerms] = useState("Net30");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setDueDate(new Date(order.expectedDeliveryDate).toISOString().slice(0, 10));
    setTaxRate(String(defaultVatRate));
    setDiscountAmount("0");
    setTerms("Net30");
    setNotes("");
  }, [defaultVatRate, open, order.expectedDeliveryDate]);

  const handleSubmit = () => {
    if (!dueDate) {
      return;
    }

    generateMutation.mutate(
      {
        orderId: order.id,
        dueDate: new Date(dueDate).toISOString(),
        taxRate: Number(taxRate) || 0,
        discountAmount: Number(discountAmount) || 0,
        terms: terms.trim() || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: (invoice) => {
          notification.success(`Generated invoice ${invoice.invoiceNumber}`);
          onOpenChange(false);
          navigate(`/invoicing?tab=ar&invoiceId=${invoice.id}`);
        },
        onError: () => {
          notification.error("Failed to generate invoice from order");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate Invoice From Order</DialogTitle>
          <DialogDescription>
            Create an AR invoice from order {order.orderNumber} for {order.customerName}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Order</Label>
            <Input value={order.orderNumber} disabled />
          </div>

          <div className="space-y-2">
            <Label>Customer</Label>
            <Input value={order.customerName} disabled />
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoice-due-date">Due Date</Label>
            <DatePicker
              id="invoice-due-date"
              value={dueDate}
              onChange={setDueDate}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <div className="space-y-2">
              <Label htmlFor="invoice-tax-rate">VAT Rate (%)</Label>
              {vatRatesQuery.isLoading ? (
                <Input id="invoice-tax-rate" value="Loading VAT rates..." disabled />
              ) : (
                <Select value={taxRate} onValueChange={setTaxRate}>
                  <SelectTrigger id="invoice-tax-rate">
                    <SelectValue placeholder="Select VAT rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {vatRatesQuery.data?.map((rate) => (
                      <SelectItem key={`${rate.name}-${rate.rate}`} value={String(rate.rate)}>
                        {`${rate.name} (${rate.rate}%)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-discount">Discount Amount</Label>
              <Input
                id="invoice-discount"
                type="number"
                min={0}
                step="0.01"
                value={discountAmount}
                onChange={(event) => setDiscountAmount(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoice-terms">Terms</Label>
            {paymentTermsQuery.isLoading ? (
              <Input id="invoice-terms" value="Loading payment terms..." disabled />
            ) : (
              <Select value={terms} onValueChange={setTerms}>
                <SelectTrigger id="invoice-terms">
                  <SelectValue placeholder="Select payment terms" />
                </SelectTrigger>
                <SelectContent>
                  {paymentTermsQuery.data?.map((term) => (
                    <SelectItem key={term.value} value={term.value}>
                      {term.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoice-notes">Notes</Label>
            <Textarea
              id="invoice-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional invoice notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={generateMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={generateMutation.isPending || !dueDate}>
            {generateMutation.isPending ? "Generating..." : "Generate Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
